import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, injectGlobal } from 'styled-components';
import ReactModal from 'react-modal';
import { font, palette } from 'styled-theme';

import Heading from '../../atoms/Heading';
import IconButton from '../../molecules/IconButton';

injectGlobal`
  body.ReactModal__Body--open {
    overflow: hidden;
  }
`;

const overlayStyles = css`
  position: fixed;
  background-color: rgba(0, 0, 0, 0.5);
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 9999;
  transition: opacity 250ms ease-in-out;
  opacity: 0;
  &[class*="after-open"] {
    opacity: 1;
  }
  &[class*="before-close"] {
    opacity: 0;
  }
`;

const ModalBox = styled(ReactModal)`
  position: absolute;
  display: flex;
  flex-direction: column;
  font-family: ${font('primary')};
  font-size: 1rem;
  background-color: ${palette('grayscale', 0, true)};
  border-radius: 0.125em;
  color: ${palette('grayscale', 0)};
  top: calc(50% - 1rem);
  left: calc(50% - 1rem);
  right: auto;
  bottom: auto;
  margin: 1rem calc(-50% + 1rem) 1rem 1rem;
  transform: translate(-50%, 100%);
  transition: transform 250ms ease-in-out;
  outline: none;
  box-sizing: border-box;
  min-width: 950px;
  width: 100%;
  max-width: calc(1300px - 1rem);
  max-height: calc(100% - 1rem);
  padding-top: ${({ hasHeader }) => hasHeader ? 0 : '1rem'};
  @media screen and (max-width: 1024px) {
    width: calc(100% - 1rem);
    min-width: 950px;
  }
  &[class*="after-open"] {
    transform: translate(-50%, -50%);
  }
  &[class*="before-close"] {
    transform: translate(-50%, 100%);
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background-color: #004a93;
  color: #fff;
  margin-top: -1px;
  > *:first-child {
    flex: 1;
  }
`;

const StyledHeading = styled(Heading)`
  margin: 0 1rem 0 0;
  font-size: 1.5em;
  line-height: 1.5em;
  font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: #fff;
`;

const Content = styled.div`
  position: relative;
  overflow: auto;
  padding: 0 1rem;
`;

const StyledIconButton = styled(IconButton)`
  border: none;
  width: 40px;
  height: 40px;
  margin-top: 0px;
  &:hover {
    opacity: 0.3;
  }
`;

const StyledReactModal = styled(({ className, ...props }) =>
  <ModalBox overlayClassName={className} closeTimeoutMS={250} {...props} />,
)`${overlayStyles}`;

const Modal = ({ children, title, closeable, onClose, ...props }) => {
  const hasHeader = title || closeable;
  return (
    <StyledReactModal
      contentLabel={title || 'Modal'}
      onRequestClose={onClose}
      hasHeader={hasHeader}
      {...props}
    >
      {hasHeader &&
        <Header>
          <StyledHeading level={2} reverse={props.reverse}>{title}</StyledHeading>
          {closeable && <StyledIconButton icon="close" onClick={onClose} palette="white" transparent iconHeight={66} />}
        </Header>
      }
      <Content>
        {children}
      </Content>
    </StyledReactModal>
  );
};

Modal.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  closeable: PropTypes.bool,
  reverse: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default Modal;



// WEBPACK FOOTER //
// ./src/shared-components/molecules/Modal/index.js